// Where a story is, from the place its text names: a port of atlas-geo `place.rs` (`findPlace`, a
// country and city centroid table, longest name first; `matchSite`, `placeInText`: a watched
// facility first, else a country, city or exposure region, the longer name winning between the
// last two, so the Gulf of Mexico is not Mexico). Tables indexed by first word, as in atlas.
//
// Added here, every addition marked:
// - Guards on names that are also words or people: Turkey (the bird), China (the porcelain),
//   Jordan (Peterson), Georgia (the US state: the country needs Caucasus context), Chile (the
//   pepper). A guarded name must be capitalised; stop phrases (stops.ts) take care of the rest.
// - A few cities and names headlines use (Türkiye, Kremlin, Taipei, Riyadh, the Middle East...).
// - Central banks as their regions (ECB, Bank of Japan, Bank of England, the Fed).
// - A last tier read only when nothing above names a place: what headlines say instead of a
//   country (Chinese, Iranian, US, UK, EU). Capitalised, and the longest wins.
// - The region a place is in: by name (Iran, Russia, Euro area...), else by distance.

import type { Place } from "./types.ts";
import { REGIONS, REGION_BY_ID, regionAt, regionRef, type Region } from "./regions.ts";
import { maskStops } from "./stops.ts";
import { asciiLower, clean, headOf, isSpaceCode, isUpperCode, isWordCode, shouted } from "./text.ts";

/* ── tables ───────────────────────────────────────────────────────── */

/** A facility a headline can name (atlas `WATCHED`, the divergence engine's watch table). */
const WATCHED: readonly (readonly [id: string, name: string, lat: number, lon: number])[] = [
  ["hormuz", "Strait of Hormuz", 26.57, 56.25],
  ["ras-tanura", "Ras Tanura", 26.64, 50.16],
  ["abqaiq", "Abqaiq Processing", 25.93, 49.67],
  ["port-arthur", "Port Arthur", 29.87, -93.93],
  ["baytown", "Baytown", 29.75, -95.01],
  ["freeport-lng", "Freeport LNG", 28.94, -95.31],
  ["sabine-pass", "Sabine Pass LNG", 29.73, -93.87],
  ["henry-hub", "Henry Hub", 30.0, -92.1],
  ["permian", "Permian Basin", 31.9, -102.3],
  ["rotterdam", "Rotterdam", 51.95, 4.14],
  ["jamnagar", "Jamnagar", 22.35, 69.85],
  ["suez", "Suez Canal", 30.42, 32.35],
  ["bab-el-mandeb", "Bab el-Mandeb", 12.58, 43.33],
  ["malacca", "Strait of Malacca", 2.5, 101.0],
  ["escondida", "Escondida", -24.27, -69.07],
  ["collahuasi", "Collahuasi", -20.98, -68.7],
  ["grasberg", "Grasberg", -4.06, 137.11],
  ["antofagasta-port", "Antofagasta", -23.65, -70.4],
];

/** What headlines call a watched site besides its name (atlas `SITE_ALIASES`). */
const SITE_ALIASES: Readonly<Record<string, readonly string[]>> = {
  hormuz: ["Hormuz"],
  abqaiq: ["Abqaiq"],
  "bab-el-mandeb": ["Bab al-Mandab", "Bab el Mandeb", "Bab al Mandab"],
  malacca: ["Malacca"],
  suez: ["Suez"],
  permian: ["Permian"],
};

/** `[name, lat, lon]`: countries first, then high-frequency cities (atlas `TABLE`, same order). */
const TABLE: readonly (readonly [string, number, number])[] = [
  ["united states", 39.8, -98.6], ["u.s.", 39.8, -98.6], ["america", 39.8, -98.6],
  ["united kingdom", 54.0, -2.0], ["britain", 54.0, -2.0], ["england", 52.5, -1.5],
  ["russia", 61.5, 105.3], ["ukraine", 48.4, 31.2], ["china", 35.9, 104.2],
  ["india", 22.6, 79.0], ["japan", 36.2, 138.3], ["germany", 51.2, 10.4],
  ["france", 46.6, 2.2], ["italy", 41.9, 12.6], ["spain", 40.0, -3.7],
  ["israel", 31.4, 35.0], ["palestine", 31.9, 35.2], ["gaza", 31.5, 34.45],
  ["iran", 32.4, 53.7], ["iraq", 33.2, 43.7], ["syria", 35.0, 38.0],
  ["lebanon", 33.9, 35.9], ["saudi arabia", 24.0, 45.0], ["saudi", 24.0, 45.0],
  ["yemen", 15.6, 48.0], ["turkey", 39.0, 35.2], ["egypt", 26.8, 30.8],
  ["libya", 26.3, 17.2], ["sudan", 15.5, 30.2], ["ethiopia", 9.1, 40.5],
  ["nigeria", 9.1, 8.7], ["south africa", -30.6, 22.9], ["kenya", -0.0, 37.9],
  ["congo", -2.9, 23.7], ["mali", 17.6, -4.0], ["niger", 17.6, 8.1],
  ["pakistan", 30.4, 69.3], ["afghanistan", 33.9, 67.7], ["bangladesh", 23.7, 90.4],
  ["myanmar", 21.9, 95.9], ["thailand", 15.9, 100.9], ["vietnam", 14.1, 108.3],
  ["indonesia", -0.8, 113.9], ["philippines", 12.9, 121.8], ["malaysia", 4.2, 101.9],
  ["south korea", 36.5, 127.9], ["north korea", 40.3, 127.5], ["korea", 37.5, 127.5],
  ["taiwan", 23.7, 121.0], ["australia", -25.3, 133.8], ["new zealand", -41.8, 172.8],
  ["canada", 56.1, -106.3], ["mexico", 23.6, -102.5], ["brazil", -14.2, -51.9],
  ["argentina", -38.4, -63.6], ["chile", -35.7, -71.5], ["colombia", 4.6, -74.3],
  ["venezuela", 6.4, -66.6], ["peru", -9.2, -75.0], ["cuba", 21.5, -77.8],
  ["haiti", 19.0, -72.3], ["poland", 51.9, 19.1], ["sweden", 60.1, 18.6],
  ["norway", 60.5, 8.5], ["finland", 61.9, 25.7], ["netherlands", 52.1, 5.3],
  ["belgium", 50.5, 4.5], ["switzerland", 46.8, 8.2], ["austria", 47.5, 14.6],
  ["greece", 39.1, 21.8], ["ireland", 53.4, -8.2], ["portugal", 39.4, -8.2],
  ["romania", 45.9, 25.0], ["hungary", 47.2, 19.5], ["czech", 49.8, 15.5],
  ["serbia", 44.0, 21.0], ["belarus", 53.7, 27.95], ["georgia", 42.3, 43.4],
  ["armenia", 40.1, 45.0], ["azerbaijan", 40.1, 47.6], ["kazakhstan", 48.0, 66.9],
  ["qatar", 25.4, 51.2], ["uae", 23.4, 53.8], ["emirates", 23.4, 53.8],
  ["jordan", 30.6, 36.2], ["morocco", 31.8, -7.1], ["algeria", 28.0, 1.7],
  ["tunisia", 33.9, 9.6], ["somalia", 5.2, 46.2], ["venezuela", 6.4, -66.6],
  // cities
  ["washington", 38.9, -77.0], ["new york", 40.7, -74.0], ["london", 51.5, -0.1],
  ["moscow", 55.75, 37.6], ["kyiv", 50.45, 30.5], ["beijing", 39.9, 116.4],
  ["tokyo", 35.7, 139.7], ["paris", 48.85, 2.35], ["berlin", 52.5, 13.4],
  ["tehran", 35.7, 51.4], ["jerusalem", 31.8, 35.2], ["istanbul", 41.0, 28.95],
  ["hong kong", 22.3, 114.2], ["seoul", 37.6, 127.0], ["delhi", 28.6, 77.2],
  ["dubai", 25.2, 55.3], ["singapore", 1.35, 103.8],
];

/** Added: more places headlines name, `[name, lat, lon, shown as]`. After atlas's table, so
 * atlas's names rank first among names of one length. */
const ADDED: readonly (readonly [string, number, number, string])[] = [
  ["u.s", 39.8, -98.6, "United States"],
  ["türkiye", 39.0, 35.2, "Turkey"], ["turkiye", 39.0, 35.2, "Turkey"],
  ["european union", 50.85, 4.35, "European Union"], ["europe", 50.0, 10.0, "Europe"],
  ["middle east", 29.0, 42.0, "Middle East"], ["mideast", 29.0, 42.0, "Middle East"],
  ["south china sea", 12.0, 114.0, "South China Sea"], ["east china sea", 29.0, 125.0, "East China Sea"],
  ["black sea", 43.4, 34.0, "Black Sea"],
  ["kremlin", 55.75, 37.62, "Moscow"], ["kiev", 50.45, 30.5, "Kyiv"],
  ["kharkiv", 49.99, 36.23, "Kharkiv"], ["odesa", 46.48, 30.72, "Odesa"],
  ["crimea", 45.3, 34.4, "Crimea"], ["donbas", 48.0, 37.8, "Donbas"], ["donbass", 48.0, 37.8, "Donbas"],
  ["tbilisi", 41.7, 44.8, "Tbilisi"],
  ["taipei", 25.03, 121.56, "Taipei"], ["shanghai", 31.23, 121.47, "Shanghai"], ["shenzhen", 22.54, 114.06, "Shenzhen"],
  ["riyadh", 24.71, 46.68, "Riyadh"], ["abu dhabi", 24.45, 54.38, "Abu Dhabi"], ["doha", 25.29, 51.53, "Doha"],
  ["baghdad", 33.31, 44.36, "Baghdad"], ["damascus", 33.51, 36.29, "Damascus"], ["beirut", 33.89, 35.5, "Beirut"],
  ["tel aviv", 32.08, 34.78, "Tel Aviv"], ["caracas", 10.49, -66.88, "Caracas"], ["cairo", 30.04, 31.24, "Cairo"],
  ["lagos", 6.52, 3.38, "Lagos"], ["johannesburg", -26.2, 28.05, "Johannesburg"], ["ankara", 39.93, 32.86, "Ankara"],
  ["brussels", 50.85, 4.35, "Brussels"], ["frankfurt", 50.11, 8.68, "Frankfurt"], ["amsterdam", 52.37, 4.9, "Amsterdam"],
  ["rome", 41.9, 12.5, "Rome"], ["madrid", 40.42, -3.7, "Madrid"], ["lisbon", 38.72, -9.14, "Lisbon"],
  ["athens", 37.98, 23.73, "Athens"], ["dublin", 53.35, -6.26, "Dublin"], ["warsaw", 52.23, 21.01, "Warsaw"],
  ["zurich", 47.37, 8.54, "Zurich"], ["geneva", 46.2, 6.15, "Geneva"], ["davos", 46.8, 9.84, "Davos"],
  ["mumbai", 19.08, 72.88, "Mumbai"], ["islamabad", 33.68, 73.05, "Islamabad"], ["karachi", 24.86, 67.0, "Karachi"],
  ["jakarta", -6.21, 106.85, "Jakarta"], ["manila", 14.6, 120.98, "Manila"], ["bangkok", 13.76, 100.5, "Bangkok"],
  ["toronto", 43.65, -79.38, "Toronto"], ["ottawa", 45.42, -75.7, "Ottawa"], ["mexico city", 19.43, -99.13, "Mexico City"],
  ["sao paulo", -23.55, -46.63, "São Paulo"], ["são paulo", -23.55, -46.63, "São Paulo"],
  ["buenos aires", -34.6, -58.38, "Buenos Aires"], ["houston", 29.76, -95.37, "Houston"],
  ["chicago", 41.88, -87.63, "Chicago"], ["san francisco", 37.77, -122.42, "San Francisco"],
];

/** What headlines call an exposure region besides its name (atlas `REGION_ALIASES`; the central
 * banks are added). */
const REGION_ALIASES: Readonly<Record<string, readonly string[]>> = {
  "bab-el-mandeb": ["Gulf of Aden"],
  panama: ["Panama"],
  "persian-gulf": ["Arabian Gulf"],
  levant: ["Levant", "West Bank"],
  "west-siberia": ["Siberia"],
  "us-gulf": ["Gulf of Mexico", "Gulf Coast"],
  alberta: ["Alberta", "oil sands"],
  copperbelt: ["Zambia", "Katanga"],
  shenzhen: ["Shenzhen"],
  "euro-area": ["eurozone", "euro zone", "European Central Bank", "ECB"],
  japan: ["Bank of Japan", "BoJ", "BOJ"],
  uk: ["Bank of England", "BoE", "BOE"],
  us: ["Federal Reserve", "Fed", "FOMC"],
};

/** Added: the last tier, what headlines say instead of a country: `[name, the TABLE name whose
 * place it is]`. Capitalised only. */
const ADJECTIVES: readonly (readonly [string, string])[] = [
  ["chinese", "china"], ["japanese", "japan"], ["russian", "russia"], ["ukrainian", "ukraine"],
  ["iranian", "iran"], ["israeli", "israel"], ["iraqi", "iraq"], ["syrian", "syria"], ["lebanese", "lebanon"],
  ["yemeni", "yemen"], ["houthi", "yemen"], ["houthis", "yemen"], ["emirati", "uae"], ["qatari", "qatar"],
  ["turkish", "turkey"], ["egyptian", "egypt"], ["libyan", "libya"], ["nigerian", "nigeria"],
  ["south african", "south africa"], ["venezuelan", "venezuela"], ["mexican", "mexico"], ["canadian", "canada"],
  ["brazilian", "brazil"], ["argentine", "argentina"], ["argentinian", "argentina"], ["chilean", "chile"],
  ["peruvian", "peru"], ["colombian", "colombia"], ["cuban", "cuba"], ["indian", "india"], ["pakistani", "pakistan"],
  ["afghan", "afghanistan"], ["taiwanese", "taiwan"], ["south korean", "south korea"],
  ["north korean", "north korea"], ["korean", "korea"], ["german", "germany"], ["french", "france"],
  ["italian", "italy"], ["spanish", "spain"], ["british", "britain"], ["dutch", "netherlands"],
  ["swiss", "switzerland"], ["norwegian", "norway"], ["swedish", "sweden"], ["polish", "poland"],
  ["greek", "greece"], ["kazakh", "kazakhstan"], ["australian", "australia"], ["indonesian", "indonesia"],
  ["vietnamese", "vietnam"], ["thai", "thailand"], ["philippine", "philippines"], ["filipino", "philippines"],
  ["malaysian", "malaysia"], ["belarusian", "belarus"], ["palestinian", "palestine"], ["sudanese", "sudan"],
  ["ethiopian", "ethiopia"], ["kenyan", "kenya"], ["congolese", "congo"], ["somali", "somalia"],
  ["algerian", "algeria"], ["moroccan", "morocco"], ["jordanian", "jordan"], ["armenian", "armenia"],
  ["azerbaijani", "azerbaijan"], ["hungarian", "hungary"], ["romanian", "romania"], ["serbian", "serbia"],
  ["austrian", "austria"], ["belgian", "belgium"], ["irish", "ireland"], ["portuguese", "portugal"],
  ["finnish", "finland"], ["american", "united states"], ["americans", "united states"],
  ["US", "united states"], ["USA", "united states"], ["UK", "britain"],
  ["european", "europe"], ["EU", "european union"],
];

/** Added: what keeps a name from being read as the place. */
type Guard = {
  /** The text writes it with a capital (Turkey, not turkey). */
  cap?: true;
  /** The text writes it exactly as the name is written (US, not us). */
  exact?: true;
  /** Not in a shouted headline, where casing says nothing. */
  plain?: true;
  /** Only when the text also says this. */
  needs?: RegExp;
  /** Never when the text also says this. */
  not?: RegExp;
};

const GUARDS: Readonly<Record<string, Guard>> = {
  turkey: { cap: true, not: /\b(?:thanksgiving|recipes?|roast(?:ed|ing)?|gravy|stuffing|gobble|carv(?:e|ing)|brin(?:e|ing)|feast)\b/i },
  china: { cap: true },
  jordan: { cap: true },
  chile: { cap: true },
  america: { cap: true },
  niger: { cap: true },
  mali: { cap: true },
  georgia: { cap: true, needs: /\b(?:tbilisi|georgian|caucasus|abkhaz\w*|ossetia\w*|kobakhidze|ivanishvili|zourabichvili|kavelashvili)\b/i },
  europe: { cap: true },
  fed: { exact: true, plain: true },
  fomc: { exact: true },
  ecb: { exact: true },
  boj: { exact: true },
  boe: { exact: true },
  us: { exact: true, plain: true },
  usa: { exact: true, plain: true },
  uk: { exact: true, plain: true },
  eu: { exact: true, plain: true },
};

/** Added: the region a place is in by its name, where distance gets it wrong (null: none). */
const PLACE_REGION: Readonly<Record<string, string | null>> = {
  "european union": "euro-area",
  europe: "euro-area",
  spain: "euro-area",
  madrid: "euro-area",
  portugal: "euro-area",
  lisbon: "euro-area",
  greece: "euro-area",
  athens: "euro-area",
  ireland: "euro-area",
  dublin: "euro-area",
  finland: "euro-area",
  // atlas's United States circle (2,300 km) takes in Mexico
  mexico: null,
  "mexico city": null,
  switzerland: null,
  zurich: null,
  geneva: null,
  davos: null,
};

/* ── names as whole-word terms ────────────────────────────────────── */

/** A name matched as atlas's `termRe`: literal words, case aside, white space in the name matching
 * any run of white space and hyphens, no word character after it. */
type Term<T> = {
  name: string;
  pieces: string[];
  value: T;
  guard: Guard | null;
};

/** Terms in the order they are tried (longest name first, table order among equals), indexed by
 * their first word. */
type Terms<T> = { list: Term<T>[]; byHead: Map<string, number[]> };

function terms<T>(names: readonly (readonly [string, T])[]): Terms<T> {
  const list: Term<T>[] = names
    .map(([name, value], order) => ({ name, value, order }))
    .sort((a, b) => b.name.length - a.name.length || a.order - b.order)
    .map(({ name, value }) => ({
      name,
      pieces: asciiLower(name).split(/\s+/).filter(Boolean),
      value,
      guard: GUARDS[asciiLower(name)] ?? null,
    }));
  const byHead = new Map<string, number[]>();
  list.forEach((term, i) => {
    const head = headOf(term.pieces[0] ?? "");
    const at = byHead.get(head);
    if (at) at.push(i);
    else byHead.set(head, [i]);
  });
  return { list, byHead };
}

/** Where the term ends when it matches at `p` in the lower-cased text, else -1. */
function endAt(pieces: readonly string[], lower: string, p: number): number {
  let q = p;
  for (let k = 0; k < pieces.length; k++) {
    const piece = pieces[k] ?? "";
    if (k > 0) {
      const from = q;
      while (q < lower.length) {
        const c = lower.charCodeAt(q);
        if (!isSpaceCode(c) && c !== 45) break;
        q++;
      }
      if (q === from) return -1;
    }
    if (!lower.startsWith(piece, q)) return -1;
    q += piece.length;
  }
  return q < lower.length && isWordCode(lower.charCodeAt(q)) ? -1 : q;
}

/** What the matchers share about one headline. */
export type Reading = { text: string; lower: string; shout: boolean };

function passes(guard: Guard | null, name: string, r: Reading, p: number, q: number): boolean {
  if (!guard) return true;
  if (guard.plain && r.shout) return false;
  if (guard.cap && !isUpperCode(r.text.charCodeAt(p))) return false;
  if (guard.exact && r.text.slice(p, q) !== name) return false;
  if (guard.needs && !guard.needs.test(r.text)) return false;
  if (guard.not && guard.not.test(r.text)) return false;
  return true;
}

/** The first term, in order, the text contains. */
function find<T>(t: Terms<T>, r: Reading): Term<T> | null {
  const { lower } = r;
  let best = -1;
  for (let p = 0; p < lower.length; p++) {
    if (!isWordCode(lower.charCodeAt(p)) || (p > 0 && isWordCode(lower.charCodeAt(p - 1)))) continue;
    let e = p;
    while (e < lower.length && isWordCode(lower.charCodeAt(e))) e++;
    const candidates = t.byHead.get(lower.slice(p, e));
    if (!candidates) continue;
    for (const i of candidates) {
      if (best >= 0 && best <= i) break;
      const term = t.list[i];
      if (!term) continue;
      const q = endAt(term.pieces, lower, p);
      if (q >= 0 && passes(term.guard, term.name, r, p, q)) {
        best = i;
        break;
      }
    }
  }
  return best >= 0 ? (t.list[best] ?? null) : null;
}

/* ── the indexes, built once ──────────────────────────────────────── */

type Spot = { name: string; lat: number; lon: number; region: Region | null };

/** A table name as a place shows it: title case, a short all-letter name in capitals (UAE). */
function displayName(name: string): string {
  if (name.length <= 3 && /^[a-z]+$/.test(name)) return name.toUpperCase();
  return name.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Atlas names a place by the table's first name at its coordinates (the U.S. and America are the
 * United States, Britain the United Kingdom). */
function tableSpot(name: string, lat: number, lon: number): Spot {
  const first = TABLE.find((e) => e[1] === lat && e[2] === lon)?.[0] ?? name;
  return { name: displayName(first), lat, lon, region: null };
}

const SPOT_BY_TABLE_NAME = new Map<string, Spot>(TABLE.map(([name, lat, lon]) => [name, tableSpot(name, lat, lon)]));
SPOT_BY_TABLE_NAME.set("europe", { name: "Europe", lat: 50.0, lon: 10.0, region: null });
SPOT_BY_TABLE_NAME.set("european union", { name: "European Union", lat: 50.85, lon: 4.35, region: null });

const SITES = terms<Spot>(
  WATCHED.flatMap(([id, name, lat, lon]) => {
    const spot: Spot = { name, lat, lon, region: null };
    return [name, ...(SITE_ALIASES[id] ?? [])].map((n) => [n, spot] as const);
  }),
);

const PLACES = terms<Spot>([
  ...TABLE.map(([name]) => [name, SPOT_BY_TABLE_NAME.get(name) as Spot] as const),
  ...ADDED.map(([name, lat, lon, shown]) => [name, { name: shown, lat, lon, region: null } as Spot] as const),
]);

const REGION_TERMS = terms<Region>(
  REGIONS.flatMap((region) => [region.name, ...(REGION_ALIASES[region.id] ?? [])].map((n) => [n, region] as const)),
);

const ADJECTIVE_TERMS = terms<Spot>(
  ADJECTIVES.flatMap(([name, place]) => {
    const spot = SPOT_BY_TABLE_NAME.get(place);
    return spot ? [[name, spot] as const] : [];
  }),
);
// every adjective is a capitalised word (or written exactly, for the acronyms)
for (const term of ADJECTIVE_TERMS.list) term.guard ??= { cap: true };

const REGION_BY_NAME = new Map<string, Region>(REGIONS.map((r) => [r.name.toLowerCase(), r]));

/** The region a place is in: by its name, else the closest fit by distance. */
function regionOf(spot: Spot): Region | null {
  if (spot.region) return spot.region;
  const key = spot.name.toLowerCase();
  if (key in PLACE_REGION) {
    const id = PLACE_REGION[key];
    return id ? (REGION_BY_ID.get(id) ?? null) : null;
  }
  return REGION_BY_NAME.get(key) ?? regionAt(spot.lat, spot.lon);
}

function placeOf(spot: Spot): Place {
  const region = regionOf(spot);
  return { name: spot.name, lat: spot.lat, lon: spot.lon, region: region ? regionRef(region) : null };
}

/** `placeInText`: the place a headline names, the most precise first: a watched facility; else a
 * country, city or region, the longer name winning between them; else what it says instead of a
 * country. Null when it names none. */
export function placeIn(r: Reading): Place | null {
  if (!r.text) return null;
  const site = find(SITES, r);
  if (site) return placeOf(site.value);
  const place = find(PLACES, r);
  const region = find(REGION_TERMS, r);
  if (region && (!place || region.name.length > place.name.length)) {
    const reg = region.value;
    return placeOf({ name: reg.name, lat: reg.lat, lon: reg.lon, region: reg });
  }
  if (place) return placeOf(place.value);
  const adjective = find(ADJECTIVE_TERMS, r);
  return adjective ? placeOf(adjective.value) : null;
}

/** The place a text names, read on its own (see {@link placeIn}). */
export function placeInText(text: string): Place | null {
  const plain = clean(text);
  const masked = maskStops(plain);
  return placeIn({ text: masked, lower: asciiLower(masked), shout: shouted(plain) });
}
